import { useState, useRef, useCallback, useMemo } from "react";
import Spinner from "../../icons/Spinner";

const formatBytes = (bytes) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const getFileIcon = (type) => {
  if (type.startsWith("image/")) return "🖼";
  if (type.startsWith("video/")) return "🎬";
  if (type.startsWith("audio/")) return "🎵";
  if (type.includes("pdf")) return "📄";
  if (type.includes("zip") || type.includes("tar") || type.includes("rar")) return "🗜";
  if (type.includes("spreadsheet") || type.includes("excel") || type.includes("csv")) return "📊";
  if (type.includes("document") || type.includes("word")) return "📝";
  if (type.includes("text")) return "📃";
  return "📁";
};

const getStatusColor = (status) => {
  switch (status) {
    case "uploading": return "#3b82f6";
    case "done": return "#22c55e";
    case "error": return "#ef4444";
    default: return "#9ca3af";
  }
};

function FileRow({ file, onRemove }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px 14px",
      background: "#fff",
      border: "1px solid #f0f0f0",
      borderRadius: "10px",
      marginBottom: "8px",
      transition: "box-shadow 0.2s",
    }}>
      <span style={{ fontSize: "22px", flexShrink: 0 }}>{getFileIcon(file.type)}</span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "5px" }}>
          <p style={{
            fontSize: "14px",
            fontWeight: 500,
            color: "#1a1a1a",
            margin: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "260px",
          }}>
            {file.name}
          </p>
          <span style={{ fontSize: "12px", color: "#9ca3af", flexShrink: 0, marginLeft: "8px" }}>
            {formatBytes(file.size)}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{ flex: 1, height: "4px", background: "#f1f5f9", borderRadius: "99px", overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: `${file.progress >= 100 ? 100 : file.progress}%`,
              background: getStatusColor(file.status),
              borderRadius: "99px",
              transition: "width 0.3s ease",
            }} />
          </div>
          <span style={{ fontSize: "11px", color: getStatusColor(file.status), fontWeight: 600, minWidth: "36px", textAlign: "right" }}>
            {file.status === "done" ? "Done" : file.status === "error" ? "Error" : `${file.progress}%`}
          </span>
        </div>
      </div>

      {file.progress === 0 && <button
        onClick={() => onRemove(file.id)}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#d1d5db",
          fontSize: "18px",
          lineHeight: 1,
          padding: "4px",
          borderRadius: "6px",
          flexShrink: 0,
          transition: "color 0.15s",
        }}
        onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
        onMouseLeave={e => e.currentTarget.style.color = "#d1d5db"}
        aria-label="Remove file"
      >
        ×
      </button>}
      {file.progress > 0 && <Spinner height={16} width={16} color="rgb(59 130 246)" />}
    </div>
  );
}

export interface IFileEntry {
  id: string;
  name: string;
  size: number;
  type: string;
  progress: number;
  status: string;
  file: File;
}

export default function FileUpload({
  accept = "*/*",
  multiple = true,
  maxSizeMB = 10,
  progress = 0,
  onFilesChanged = (files: IFileEntry[]) => {},
}) {
  const [files, setFiles] = useState<IFileEntry[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const addFiles = useCallback((rawFiles: FileList) => {
    const maxBytes = maxSizeMB * 1024 * 1024;
    const newFiles = Array.from(rawFiles).map((f) => {
      const id = `${f.name}-${Date.now()}-${Math.random()}`;
      const oversized = f.size > maxBytes;
      const entry = {
        id,
        name: f.name,
        size: f.size,
        type: f.type,
        progress: oversized ? 0 : 0,
        status: oversized ? "error" : "uploading",
        file: f,
      } as IFileEntry;
      // if (!oversized) simulateUpload(id);
      return entry;
    });
    setFiles(newFiles.slice(0, 1));
    // setFiles(prev => multiple ? [...prev, ...newFiles] : newFiles.slice(0, 1));
  }, [maxSizeMB]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length) {
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  useMemo(() => {
    onFilesChanged(files);
  }, [files, onFilesChanged]);

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const doneCount = files.filter(f => f.status === "done").length;
  const allDone = files.length > 0 && doneCount === files.length;

  if (files.length ===0) {
    return (
      <div>
        {/* Drop Zone */}
        <div
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          style={{
            border: `2px dashed ${isDragging ? "#6366f1" : "#e2e8f0"}`,
            borderRadius: "16px",
            padding: "40px 24px",
            textAlign: "center",
            cursor: "pointer",
            background: isDragging ? "#f5f3ff" : "#fafafa",
            transition: "all 0.2s ease",
            userSelect: "none",
            marginBottom: "16px",
          }}
        >
          <div style={{
            width: "56px",
            height: "56px",
            borderRadius: "14px",
            background: isDragging ? "#e0e7ff" : "#f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
            fontSize: "26px",
            transition: "background 0.2s",
          }}>
            ☁️
          </div>
          <p style={{ margin: "0 0 4px", fontSize: "15px", fontWeight: 600, color: "#1e293b" }}>
            {isDragging ? "Drop file here" : "Drag & drop file"}
          </p>
          <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#94a3b8" }}>
            or click to browse — up to {maxSizeMB}MB each
          </p>
          <span style={{
            display: "inline-block",
            padding: "8px 20px",
            background: "#6366f1",
            color: "#fff",
            borderRadius: "8px",
            fontSize: "13px",
            fontWeight: 600,
            letterSpacing: "0.02em",
          }}>
            Browse files
          </span>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            style={{ display: "none" }}
            onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ""; }}
          />
        </div>
      </div>
    );
  }

  return (
    <div 
      // style={{ fontFamily: "'Inter', sans-serif", maxWidth: "480px", margin: "0 auto" }}
    >
      <div>
        {/* <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
          <p style={{ margin: 0, fontSize: "13px", color: "#64748b", fontWeight: 500 }}>
            {doneCount}/{files.length} uploaded
          </p>
          <button
            onClick={() => setFiles([])}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "12px",
              color: "#94a3b8",
              padding: "2px 6px",
            }}
          >
            Clear all
          </button>
        </div> */}

        {files.map(file => {
          file.progress = progress;
          return <FileRow key={file.id} file={file} onRemove={removeFile} />;
        })}

        {allDone && (
          <button
            onClick={() => onFilesChanged?.(files)}
            style={{
              width: "100%",
              marginTop: "12px",
              padding: "12px",
              background: "#22c55e",
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "opacity 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.9"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >
            ✓ All files uploaded
          </button>
        )}
      </div>
    </div>
  );
}
